import type { ReactNode } from 'react';

import { Button, Card, Field, Input, MonoLabel, OptionGroup, OptionRow } from '@/design-system';
import { candidateKey, normalizePostcode, type PropertyCandidate } from '@/entities/property';

import { clientAddressCopy, type AddressLookupCopy } from './copy';
import type { AddressLookup } from './useAddressLookup';

export type AddressFieldName = 'postcode' | 'houseNumber' | 'suffix' | 'street' | 'city';

export interface AddressLookupFieldsProps {
    address: AddressLookup;
    copy: AddressLookupCopy;
    /**
     * Het gevonden adres. Zonder deze prop een kaart met straat, nummer, postcode en
     * plaats; het object zet er oppervlakte, bouwjaar en gebruiksdoel bij.
     */
    renderFound?: (candidate: PropertyCandidate, onChooseAnother: (() => void) | null) => ReactNode;
    /** Een tweede regel onder elk adres in de keuzelijst. */
    describeCandidate?: (candidate: PropertyCandidate) => string;
    /** Het hele adres zelf laten invullen zonder opzoeken, zoals een adres in het buitenland. */
    allowManualEntry?: boolean;
    /** Meldingen van de server, onder het veld waar ze over gaan. */
    errors?: Partial<Record<AddressFieldName, string>>;
}

/**
 * Postcode, huisnummer en toevoeging, en daaronder wat de BAG erover zegt.
 *
 * Elke toestand is een rustige regel of een kaart, nooit een rode melding: een
 * adres dat de BAG niet kent of een BAG die niet antwoordt, leidt naar straat en
 * plaats zelf invullen. Alles is minstens 44 px hoog, ook de adresregels in de
 * keuzelijst. Het object van een keuring en het adres van een klant tonen
 * hetzelfde blok; alleen wat er bij een gevonden adres staat verschilt.
 */
export function AddressLookupFields({
    address,
    copy,
    renderFound,
    describeCandidate,
    allowManualEntry = false,
    errors = {},
}: AddressLookupFieldsProps) {
    return (
        <>
            <div className="grid grid-cols-[1.2fr_1fr_0.8fr] gap-3">
                <Field label={copy.postcode} error={errors.postcode}>
                    <Input
                        value={address.postcode}
                        autoComplete="postal-code"
                        onChange={(event) => address.setPostcode(event.target.value)}
                    />
                </Field>
                <Field label={copy.houseNumber} error={errors.houseNumber}>
                    <Input
                        value={address.houseNumber}
                        inputMode={address.status === 'manualEntry' ? 'text' : 'numeric'}
                        onChange={(event) => address.setHouseNumber(event.target.value)}
                    />
                </Field>
                <Field label={copy.suffix} error={errors.suffix}>
                    <Input
                        value={address.suffix}
                        onChange={(event) => address.setSuffix(event.target.value)}
                    />
                </Field>
            </div>

            <div aria-live="polite" className="grid min-w-0 gap-3">
                <AddressState
                    address={address}
                    copy={copy}
                    renderFound={renderFound}
                    describeCandidate={describeCandidate}
                    allowManualEntry={allowManualEntry}
                    errors={errors}
                />
            </div>
        </>
    );
}

/** Het adres van de klant zelf: straat en plaats uit de BAG, of zelf ingevuld, ook in het buitenland. */
export function ClientAddressFields({
    address,
    errors,
}: {
    address: AddressLookup;
    errors?: Partial<Record<AddressFieldName, string>>;
}) {
    return (
        <AddressLookupFields
            address={address}
            copy={clientAddressCopy}
            allowManualEntry
            errors={errors}
        />
    );
}

function AddressState({
    address,
    copy,
    renderFound,
    describeCandidate,
    allowManualEntry,
    errors,
}: Required<Pick<AddressLookupFieldsProps, 'address' | 'copy' | 'allowManualEntry' | 'errors'>> &
    Pick<AddressLookupFieldsProps, 'renderFound' | 'describeCandidate'>) {
    if (address.status === 'typing') {
        return allowManualEntry && notLookingDutch(address) ? (
            <>
                <Note tone="quiet">{copy.abroad}</Note>
                <Button
                    variant="outline"
                    size="md"
                    className="justify-self-start"
                    onClick={() => address.enterManually(true)}
                >
                    {copy.typeManually}
                </Button>
            </>
        ) : (
            <Note tone="quiet">{copy.hint}</Note>
        );
    }

    if (address.status === 'searching') {
        return (
            <MonoLabel size={11} tone="muted" as="p" className="m-0">
                {copy.searching}
            </MonoLabel>
        );
    }

    if (address.status === 'found' && address.selected) {
        const onChooseAnother = address.candidates.length > 1 ? address.chooseAnother : null;

        return renderFound ? (
            renderFound(address.selected, onChooseAnother)
        ) : (
            <FoundAddressCard
                candidate={address.selected}
                eyebrow={copy.found}
                otherLabel={copy.other}
                onChooseAnother={onChooseAnother}
            />
        );
    }

    if (address.status === 'choose' && !address.typingManually) {
        return (
            <>
                <Note>
                    {address.suffixMatched
                        ? copy.choose(address.candidates.length)
                        : copy.suffixUnknown(address.suffix.trim())}
                </Note>
                <Card padding="none" radius={9} clip>
                    <OptionGroup
                        type="radio"
                        label={copy.choices}
                        hasSelection={false}
                        className="max-h-[296px] overflow-y-auto"
                    >
                        {address.candidates.map((candidate, index) => (
                            <OptionRow
                                key={candidateKey(candidate)}
                                index={index}
                                checked={false}
                                onSelect={() => address.choose(candidate)}
                            >
                                <span className="block text-[14px] font-medium text-[var(--text-primary)]">
                                    {streetLine(candidate)}
                                </span>
                                {describeCandidate ? (
                                    <span className="block text-[12px] text-[var(--text-tertiary-safe)]">
                                        {describeCandidate(candidate)}
                                    </span>
                                ) : null}
                            </OptionRow>
                        ))}
                    </OptionGroup>
                </Card>
                {!address.suffixMatched ? (
                    <Button
                        variant="outline"
                        size="md"
                        className="justify-self-start"
                        onClick={() => address.typeManually(true)}
                    >
                        {copy.typeManually}
                    </Button>
                ) : null}
            </>
        );
    }

    const note =
        address.status === 'unavailable'
            ? copy.unavailable
            : address.status === 'notFound'
              ? copy.notFound
              : address.status === 'manualEntry'
                ? copy.enteringManually
                : address.status === 'stored'
                  ? null
                  : copy.manual;

    return (
        <>
            {note ? <Note>{note}</Note> : null}
            {address.status === 'unavailable' ? (
                <Button
                    variant="outline"
                    size="md"
                    className="justify-self-start"
                    onClick={address.retry}
                >
                    {copy.retry}
                </Button>
            ) : null}
            {address.typingManually ? (
                <Button
                    variant="outline"
                    size="md"
                    className="justify-self-start"
                    onClick={() => address.typeManually(false)}
                >
                    {copy.backToChoices}
                </Button>
            ) : null}
            {address.status === 'manualEntry' ? (
                <Button
                    variant="outline"
                    size="md"
                    className="justify-self-start"
                    onClick={() => address.enterManually(false)}
                >
                    {copy.lookUp}
                </Button>
            ) : null}
            <div className="grid grid-cols-2 gap-3">
                <Field label={copy.street} error={errors.street}>
                    <Input
                        value={address.street}
                        autoComplete="address-line1"
                        onChange={(event) => address.setStreet(event.target.value)}
                    />
                </Field>
                <Field label={copy.city} error={errors.city}>
                    <Input
                        value={address.city}
                        autoComplete="address-level2"
                        onChange={(event) => address.setCity(event.target.value)}
                    />
                </Field>
            </div>
        </>
    );
}

/** Het gevonden adres op een kaart, met plaats voor wat er verder over bekend is. */
export function FoundAddressCard({
    candidate,
    eyebrow,
    otherLabel,
    onChooseAnother,
    children,
}: {
    candidate: PropertyCandidate;
    eyebrow: string;
    otherLabel: string;
    onChooseAnother: (() => void) | null;
    children?: ReactNode;
}) {
    return (
        <Card variant="accent" padding="md" radius={9}>
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                    <MonoLabel size={10} tone="muted" as="div" className="mb-1">
                        {eyebrow}
                    </MonoLabel>
                    <div className="text-[14px] font-medium text-[var(--text-primary)]">
                        {streetLine(candidate)}
                    </div>
                    <div className="text-[13px] text-[var(--text-secondary)]">
                        {`${candidate.postcode} ${candidate.city ?? ''}`.trim()}
                    </div>
                </div>
                {onChooseAnother ? (
                    <Button variant="outline" size="md" onClick={onChooseAnother}>
                        {otherLabel}
                    </Button>
                ) : null}
            </div>
            {children}
        </Card>
    );
}

/**
 * Kan dit geen Nederlands adres meer worden? Een postcode die niet als `1234 AB`
 * begint, of een huisnummer achter een postcode die niet af is. Pas dan biedt het
 * blok aan om het adres zelf in te vullen; tot die tijd is de BAG de weg.
 */
function notLookingDutch(address: AddressLookup): boolean {
    const postcode = normalizePostcode(address.postcode);

    if (postcode === '') {
        return false;
    }

    return (
        !/^[1-9](\d{0,3}|\d{3}[A-Z]{1,2})$/.test(postcode) ||
        (address.houseNumber.trim() !== '' && !/^[1-9]\d{3}[A-Z]{2}$/.test(postcode))
    );
}

/** "Stadsplateau 1-BD": de straat met het nummer zoals op de deur. */
function streetLine(candidate: PropertyCandidate): string {
    return `${candidate.street ?? ''} ${candidate.houseNumberLabel}`.trim();
}

function Note({ children, tone = 'plain' }: { children: string; tone?: 'plain' | 'quiet' }) {
    return (
        <p
            className={
                tone === 'quiet'
                    ? 'm-0 text-[12px] leading-[1.4] text-[var(--text-tertiary-safe)]'
                    : 'm-0 text-[13px] leading-[1.5] text-[var(--text-secondary)]'
            }
        >
            {children}
        </p>
    );
}
